import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { useAuthStore } from '@/stores/useAuthStore'

export const featureFlagsQueryKey = ['feature-flags'] as const

// Flags change when an operator ramps a rollout, not while someone is working.
// A long stale time keeps this off the hot path; a sign-out clears the cache.
const STALE_TIME_MS = 5 * 60 * 1000

interface ResolvedFlags {
  flags: Record<string, boolean>
  resolvedAt: string
}

/**
 * Resolved feature flags for the signed-in user's organization.
 *
 * The server decides. This hook never evaluates a rule, reads a plan or looks at
 * a percentage — doing any of that here is what the spec's "plan access must
 * resolve through a central service, never hard-coded in the frontend"
 * guardrail forbids, and it would drift from the backend the first time a rule
 * changed.
 */
export function useFeatureFlags(): {
  flags: Record<string, boolean>
  isLoading: boolean
} {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const { data, isPending } = useQuery<ResolvedFlags>({
    queryKey: featureFlagsQueryKey,
    queryFn: async () => {
      const response = await apiClient.get<{ data: ResolvedFlags }>('/feature-flags/resolved')
      return response.data.data
    },
    enabled: isAuthenticated,
    staleTime: STALE_TIME_MS,
    retry: 0,
  })

  return { flags: data?.flags ?? {}, isLoading: isPending && isAuthenticated }
}

/**
 * Whether one flag is on. Unknown keys are `false`: a feature the server has not
 * heard of has not been rolled out, and defaulting to `true` would ship an
 * unfinished feature the moment someone typo'd a key.
 */
export function useFeatureFlag(key: string): boolean {
  const { flags } = useFeatureFlags()
  return flags[key] === true
}
